import {gsap, Power0, Power1, Power4} from "gsap";

import {getRandomArbitrary, getRandomInt, getRandomNormal} from "../util/color.js";
import {
    makeCloudTextureSprite as makeCloudTexture
} from "./weather_generators/clouds.js";
import {Moon} from "./weather_generators/moon.js";
import {dateService} from "../datetime/time.js";
import {makeHailTextureSprite, makeRainTextureSprite, makeSnowFlake} from "./weather_generators/precipitation.js";
import {Sun} from "./weather_generators/sun.js";
import {celsiusToFahrenheit, getTempUnits} from "../settings/temp_units_switch";
import {getUVIColor} from "./uvi/uvi";
import {createPrecipChart, createTempChart} from "./temperature/temp_graphs";
import {autoDetectRenderer, Color, Container, Sprite} from "pixi.js";


const weather_types = ["snow", "mix-rain-snow", "sleet", "rain", "hail", "thunder", "cloud", "sun", "haze", "smoke"];


export class WeatherCard {
    constructor(container) {

        // in an object so the values can be animated in gsap
        this.settings = {
            windSpeed: 10,
            rainCount: 0,
            hailCount: 0,
            leafCount: 0,
            snowCount: 0,
            fogCount: 0,
            cloudCount: 0,
            splashBounce: 80,
            precipitation: false,
            makeSplash: false
        };

        let elem_id = container.id;
        this.container = container;
        this.card = this.container.querySelector(".card");
        this.innerSVG = this.card.querySelector(".inner");

        this.canvas = this.card.querySelector(".weather-card__canvas");
        this.scene = new Container();
        this.scene.hitArea = null;
        this.scene.sortableChildren = false;
        this.scene.eventMode = "none";
        this.particleContainer = new Container();
        this.particleContainer.zIndex = -3;
        this.scene.addChild(this.particleContainer);

        // technical
        this.lightningTimeout = 0;
        this.start = undefined;
        this.rain_count = 0;
        this.leaf_count = 0;
        this.flake_count = 0;
        this.hail_count = 0;
        this.cloud_count = 0;
        this.colorMap = null;
        this.precipitation = false;
        this.brightness = 0;

        // create sizes object, we update this later
        this.sizes = {
            container: {width: 0, height: 0},
            card: {width: 0, height: 0}
        };

        this.summary = this.card.querySelector(".weather-card__summary");
        this.date = this.card.querySelector(".weather-card__date");
        this.temp = this.card.querySelector(".weather-card__temperature");
        this.temp_min = this.card.querySelector(".weather-card__temperature-night");
        this.wind_speed_el = this.card.querySelector(".wind-speed");
        this.wind_dir_el = this.card.querySelector(".wind-direction");
        this.wind_units_el = this.card.querySelector(".wind-units");
        this.leaf = document.getElementById("leaf");
        this.moon = new Moon(elem_id, this.sizes.card);
        this.sun = new Sun(elem_id, this.sizes.card);
    }

    async resize() {
        // 📏 grab window and card sizes
        this.sizes.container.width = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
        this.sizes.card.width = this.card.clientWidth;
        this.sizes.card.height = this.card.clientHeight;
        this.sizes.card.offset = {left: this.card.offsetLeft, top: this.card.offsetTop};

        // 📐 update svg sizes
        this.innerSVG.setAttribute("width", this.sizes.card.width);
        this.innerSVG.setAttribute("height", this.sizes.card.height);

        if (this.renderer) {
            this.renderer.resize(this.sizes.card.width, this.sizes.card.height);
        } else {
            this.renderer = await autoDetectRenderer(
                {
                    "preference": "webgl",
                    "width": this.sizes.card.width,
                    "height": this.sizes.card.height,
                    "view": this.canvas, backgroundAlpha: 0
                });
        }
    }

    #updateDateText(day_str) {
        // d is a Date
        this.date.textContent = day_str;
        gsap.fromTo(this.date, {x: 100}, {delay: 2, duration: 2, opacity: 1, x: 0, ease: Power4.easeOut});
    }

    makeRain(line = null) {
        // Start the drop at a random point at the top but leaving a 10px margin
        let x = Math.random() * (this.sizes.card.width - 20) + 10;

        if (line == null) {
            line = makeRainTextureSprite();
            this.particleContainer.addChildAt(line, 0);
        }
        let tint = {...this.colorMap.rain};
        tint.l += (20*this.brightness);
        line.tint = new Color({h: tint.h, s: tint.s, l: tint.l, a: line.alpha});

        let duration;
        let x_final = x;
        if (line.alpha >= 0.5) {
            duration = 0.3 + getRandomArbitrary(0, 2) / 10;
            line.scale.set(1 / 6, this.sizes.card.height / 900);
            // only small drops move around
            x_final = x + getRandomInt(-20, 20);
        } else {
            duration = 0.6 + getRandomArbitrary(0, 2) / 10;
            // orig height 48
            let scaley = getRandomArbitrary(1 / 2, this.sizes.card.height / 24);
            line.scale.set(1 / 6, scaley);
        }

        // Define line position - this aligns the top left corner of our canvas
        line.position.x = x;
        line.position.y = -line.height;

        // add line to the scene for rendering
        this.rain_count += 1;

        // Start the falling animation, calls onRainEnd when the
        // animation finishes.
        let delay = Math.random();
        gsap.to(line,
            {
                duration: duration,
                delay: delay,
                y: this.sizes.card.height,
                x: x_final,
                ease: Power0.easeIn,
                onComplete: this.onRainEnd.bind(this),
                onCompleteParams: [line]
            });
    }

    onRainEnd(line) {
        this.rain_count -= 1;

        if (this.rain_count < this.settings.rainCount) {
            this.makeRain(line);
        } else {
            line.parent?.removeChild(line);
            line.destroy();
            line = null;
        }
    }

    makeLeaf() {
        const scale = 0.5 + Math.random() * 0.5;
        let newLeaf;

        const areaY = this.sizes.card.height / 2;
        let y = areaY + Math.random() * areaY;
        let endY = y - (Math.random() * (areaY * 2) - areaY);
        const x = -100;
        let endX;
        const colors = ['#b7a851', '#76993E', '#4A5E23', '#6D632F'];
        const color = colors[Math.floor(Math.random() * colors.length)];
        let xBezier;

        newLeaf = this.leaf.cloneNode(true);
        newLeaf.setAttribute("fill", color);
        this.innerSVG.appendChild(newLeaf);
        xBezier = this.sizes.card.width / 2;
        endX = this.sizes.card.width + 50;

        let bezier = [{x: xBezier, y: Math.random() * endY + endY / 3}, {x: endX, y: endY}];
        this.leaf_count += 1;
        gsap.fromTo(newLeaf,
            {
                rotation: Math.random() * 180,
                x: x, y: y,
                scale: scale
            },
            {
                duration: 2,
                rotation: Math.random() * 360,
                motionPath: {"path": bezier},
                onComplete: this.onLeafEnd.bind(this),
                onCompleteParams: [newLeaf],
                ease: Power0.easeIn
            });
    }

    onLeafEnd(leaf) {
        leaf.remove();
        leaf = null;
        this.leaf_count -= 1;

        if (this.leaf_count < this.settings.leafCount) {
            this.makeLeaf();
        }
    }

    makeHail(stone = null) {
        const windOffset = this.settings.windSpeed * 4;
        const offset = 0.25 * this.currentWeather.intensity;
        const scale = offset + Math.random() * offset;

        let x;
        let endY;
        const size = 5 * scale;

        if (stone == null) {
            stone = new Sprite(makeHailTextureSprite());
            this.particleContainer.addChildAt(stone, 0);
        }
        stone.tint = this.currentWeather.type === 'sleet' || this.currentWeather.type.indexOf('mix') > -1 ? "#86a3f9" : "#ffffff";
        stone.scale.set(size / 30, size / 30);

        if (size > 4) {
            x = 20 + Math.random() * (this.sizes.card.width - 40) + windOffset;
            endY = this.sizes.card.height + 10;
            x = x + this.sizes.card.offset.left;

        } else {
            x = 20 + Math.random() * (this.sizes.card.width + windOffset - 20);
            endY = this.sizes.card.height + 10;

        }
        this.hail_count += 1;

        // Start the falling animation, calls onHailEnd when the
        // animation finishes.
        gsap.fromTo(stone,
            {x: x - windOffset, y: -stone.height},
            {
                duration: 1,
                delay: Math.random(),
                y: endY, x: x,
                ease: Power0.easeNone,
                onComplete: this.onHailEnd.bind(this),
                onCompleteParams: [stone]
            }
        );
    }

    onHailEnd(stone) {
        this.hail_count -= 1;

        if (this.hail_count < this.settings.hailCount) {
            this.makeHail(stone);
        } else {
            stone.parent?.removeChild(stone);
            stone.destroy();
            stone = null;
        }
    }

    onCloudEnd(cloud) {
        this.cloud_count -= 1;

        if (this.cloud_count < this.settings.cloudCount) {
            this.makeCloud(cloud);
        } else {
            cloud.parent?.removeChild(cloud);
            cloud.destroy({children: true});
            cloud = null;
        }
    }

    makeCloud(cloud = null) {
        if (cloud == null) {
            cloud = new Sprite(makeCloudTexture());
            this.particleContainer.addChild(cloud);
        }
        cloud.tint = this.#getCloudColor();
        this.cloud_count += 1;
        let topOffset = 0;
        if (this.precipitation) {
            topOffset = -2*cloud.height/3;
        }
        cloud.alpha = getRandomArbitrary(0.5, 0.7);

        // animate clouds
        gsap.fromTo(cloud, {x: -cloud.width, y: topOffset}, {
            duration: getRandomArbitrary(100, 200) / this.settings.windSpeed,
            ease: "none",
            x: `+=${this.sizes.card.width + cloud.width}`,
            onComplete: this.onCloudEnd.bind(this),
            onCompleteParams: [cloud],
        });

    }

    makeSnow(flake = null) {
        let multiplier = this.currentWeather.intensity / 2 + 0.5;
        let r = getRandomNormal(3 * multiplier, 6 * multiplier);
        if (flake == null) {
            flake = makeSnowFlake();
            this.particleContainer.addChildAt(flake, 0);
        }
        let duration;
        if (flake.alpha >= 0.7) {
            duration = getRandomNormal(6, 10);
        } else if (flake.alpha >= 0.5) {
            duration = getRandomNormal(10, 15);
        } else {
            duration = getRandomNormal(15, 20);
        }
        flake.scale.set(r / 30, r / 30);
        let endY = this.sizes.card.height + 10;
        this.flake_count += 1;

        let x = Math.random() * this.sizes.card.width;
        gsap.fromTo(flake, {x: x, y: -flake.height}, {
            duration: duration,
            y: endY,
            onComplete: this.onSnowEnd.bind(this),
            onCompleteParams: [flake],
            ease: Power0.easeIn
        });
        gsap.to(flake, {
            duration: 3,
            x: x + (Math.random() * 150 - 75),
            repeat: Math.ceil(duration / 3),
            yoyo: true,
            ease: Power1.easeInOut
        });
    }

    onSnowEnd(flake) {
        this.flake_count -= 1;

        if (this.flake_count < this.settings.snowCount) {
            this.makeSnow(flake);
        } else {
            flake.parent?.removeChild(flake);
            gsap.killTweensOf(flake);
            flake.destroy();
            flake = null;
        }
    }

    #setSunPosition() {
        let now = dateService.getTimestampSeconds();
        while (this.dayWeather.dt - 60*60*12 > now) {
            now += 60*60*24;
        }
        this.sun.adjustPosition(this.currentWeather.type, now, this.dayWeather.sunrise, this.dayWeather.sunset);
        this.moon.adjustPosition(this.currentWeather.type, now, this.dayWeather.sunrise, this.dayWeather.sunset);
    }

    #setWindSpeed() {
        gsap.to(this.settings, {duration: 10, windSpeed: this.dayWeather.wind_speed, ease: Power0.easeNone});
    }

    #setRainCount() {
        const base_rain = {duration: 10, delay: Math.random() * 3 + 3, ease: Power0.easeNone}

        switch (this.currentWeather.type) {
            case 'mix':
            case 'mix-rain-snow':
            case 'mix-rain-sleet':
            case 'rain':
                gsap.to(this.settings, {...base_rain, rainCount: 100 * this.currentWeather.intensity});
                break;
            case 'hail':
                gsap.to(this.settings, {...base_rain, rainCount: 10 * this.currentWeather.intensity});
                break;
            case 'severe':
            case 'thunder':
                gsap.to(this.settings, {...base_rain, rainCount: 150 * this.currentWeather.intensity});
                break;
            default:
                gsap.to(this.settings, {duration: 3, rainCount: 0, ease: Power0.easeNone});
                break;
        }

    }

    #setHailCount() {
        switch (this.currentWeather.type) {
            case 'mix':
                gsap.to(this.settings, {
                    duration: 3,
                    hailCount: 3 * this.currentWeather.intensity,
                    ease: Power0.easeNone
                });
                break;
            case 'mix-snow-sleet':
            case 'mix-rain-sleet':
                gsap.to(this.settings, {
                    duration: 3,
                    hailCount: 10 * this.currentWeather.intensity,
                    ease: Power0.easeNone
                });
                break;
            case 'sleet':
                gsap.to(this.settings, {
                    duration: 3,
                    hailCount: 20 * this.currentWeather.intensity,
                    ease: Power0.easeNone
                });
                break;
            case 'severe':
                gsap.to(this.settings, {
                    duration: 3,
                    hailCount: 3 * this.currentWeather.intensity,
                    ease: Power0.easeNone
                });
                break;
            case 'hail':
                gsap.to(this.settings, {
                    duration: 3,
                    hailCount: 20 * this.currentWeather.intensity,
                    ease: Power0.easeNone
                });
                break;
            default:
                gsap.to(this.settings, {duration: 1, hailCount: 0, ease: Power0.easeNone});
                break;
        }
    }

    #setLeafCount() {
        if (this.dayWeather.wind_speed > 10) {
            gsap.to(this.settings, {
                duration: 5,
                leafCount: Math.ceil(this.dayWeather.wind_speed / 5),
                ease: Power0.easeNone
            });
        } else {
            this.settings.leafCount = 0;
        }
    }

    #setSnowCount() {
        switch (this.currentWeather.type) {

            case 'mix':
            case 'mix-rain-snow':
            case 'mix-snow-sleet':
            case 'snow':
                gsap.to(this.settings, {
                    duration: 20, delay: Math.random() * 2 + 1,
                    snowCount: 40 * this.currentWeather.intensity,
                    ease: Power0.easeNone
                });
                break;
            default:
                this.settings.snowCount = 0;
                break;
        }
    }

    #setFog() {
        if (this.currentWeather.type === 'haze' || this.currentWeather.type === 'smoke') {

        } else {

        }
    }

    #setClouds() {
        switch (this.currentWeather.type) {
            case 'cloud':
            case 'haze':
                this.precipitation = false;
                gsap.to(this.settings, {
                    duration: 5 + 5 * Math.random(),
                    cloudCount: this.currentWeather.clouds / 10,
                    ease: Power0.easeNone
                });
                break;
            case 'sun':
                this.settings.cloudCount = 0;
                this.precipitation = false;
                break;
            case 'severe':
            case 'thunder':
                this.precipitation = true;
                this.settings.cloudCount = 0;
                break;
            default:
                this.precipitation = true;
                this.settings.cloudCount = 4;
                break;
        }
    }

    enableSetting(name) {
        switch (name) {
            case "precip":
                this.#enablePrecipitation();
                break;
            case "uvi":
                this.#enableUVIndex();
                break;
            case "temp":
                this.#enableTemperature();
                break;
            case "wind":
                this.#enableWind();
                break;
            default:
                break;
        }
    }

    disableSetting(name) {
        switch (name) {
            case "precip":
                this.#disablePrecipitation();
                break;
            case "uvi":
                this.#disableUVIndex();
                break;
            case "temp":
                this.#disableTemperature();
                break;
            case "wind":
                this.#disableWind();
                break;
            default:
                break;
        }
    }

    #enablePrecipitation() {
        // Chart
        let chart_cont = this.container.querySelector('.precip-chart');
        // Remove existing canvas if it exists
        if (chart_cont.querySelector('canvas')) {
            chart_cont.removeChild(chart_cont.querySelector('canvas'));
        }

        let newCanvas = document.createElement('canvas');
        chart_cont.appendChild(newCanvas);
        createPrecipChart(newCanvas, 0, 1, this.dayWeather.hourly_pop, Math.max(0.5, this.brightness));
    }

    #enableTemperature() {
        let chart_cont = this.container.querySelector('.temp-chart');
        // Remove existing canvas if it exists
        if (chart_cont.querySelector('canvas')) {
            chart_cont.removeChild(chart_cont.querySelector('canvas'));
        }

        let newCanvas = document.createElement('canvas');
        chart_cont.appendChild(newCanvas);
        createTempChart(newCanvas, this.dayWeather.min_temp_5day - 1, this.dayWeather.max_temp_5day + 1, this.dayWeather.hourly_temp);
    }

    #disableTemperature() {
        let c = this.container.querySelector('.temp-chart canvas');
        c.style.height = 0;
    }

    #enableUVIndex() {
        let uvi_container = this.container.querySelector('.uv-index-circles');
        for (let i = 1; i <= 24; i++) {
            let uv_value = this.dayWeather.hourly_uvi[i - 1];
            let bar = uvi_container.querySelector('.weather-card__bar-hour-' + i);
            bar.style.backgroundColor = getUVIColor(uv_value);
        }
        uvi_container.classList.add("active");
    }

    #disableUVIndex() {
        this.container.querySelector('.uv-index-circles').classList.remove("active");
    }

    #enableWind() {
        this.container.querySelector('.wind-container').classList.remove("hidden");
    }

    #disableWind() {
        this.container.querySelector('.wind-container').classList.add("hidden");
    }

    #disablePrecipitation() {
        let c = this.container.querySelector('.precip-chart canvas');
        c.style.height = 0;
    }

    changeWeather(dayWeather) {
        this.reset();
        this.dayWeather = dayWeather;

        this.updateTempText();
        this.updateWindSpeed();
        this.#updateDateText(dayWeather.weekday_name);

        this.currentWeather = {
            "type": dayWeather.type,
            "intensity": dayWeather.intensity,
            "name": dayWeather.description,
            "clouds": dayWeather.clouds
        };
        gsap.to(this.summary, {
            duration: 1,
            opacity: 0,
            x: -30,
            onComplete: this.#updateSummaryText.bind(this),
            ease: Power4.easeIn
        });

        this.container.classList.add(this.currentWeather.type);
        this.moon.makeSVG(dayWeather.moon_phase);

        this.#setWindSpeed();
        this.#setRainCount();
        this.#setSnowCount();
        this.#setHailCount();
        this.#setLeafCount();
        // sun position
        this.#setSunPosition();
        this.#setFog();
        this.#setClouds();

        // lightning
        this.startLightningTimer();
    }

    adaptToDayTime(colorMap) {
        this.colorMap = colorMap;
        this.#setSunPosition();
    }

    #getCloudColor() {
        // copy
        let hsl = {...this.colorMap.cloud};
        if (this.precipitation) {
            hsl.l = Math.min(getRandomArbitrary(0.80, 0.9) * hsl.l, 100);
        } else {
            hsl.l = Math.min(getRandomArbitrary(0.98, 1.1) * hsl.l, 100);
            hsl.s = Math.min(getRandomArbitrary(0.98, 1.1) * hsl.s, 100);
        }
        return new Color({h: hsl.h, s: hsl.s, l: hsl.l});
    }

    #updateSummaryText() {
        this.summary.innerHTML = this.currentWeather.name;
        gsap.fromTo(this.summary, {x: 30}, {duration: 1.5, opacity: 1, x: 0, ease: Power4.easeOut});
    }

    updateTempText() {
        let newTemp = this.dayWeather.temp
        // backend always returns metric, convert to F if needed
        if (getTempUnits() === "F") {
            this.temp.innerText = Math.round(celsiusToFahrenheit(newTemp.max));
            this.temp_min.innerText = Math.round(celsiusToFahrenheit(newTemp.min));
        } else {
            this.temp.innerText = Math.round(newTemp.max);
            this.temp_min.innerText = Math.round(newTemp.min);
        }
    }

    updateWindSpeed() {
        if (getTempUnits() === "F") {
            this.wind_speed_el.innerText = Math.round(this.dayWeather.wind_speed*2.23694);
            this.wind_units_el.innerText = "mph";
        } else {
            this.wind_speed_el.innerText = Math.round(this.dayWeather.wind_speed);
            if (this.wind_units_el.text === "mph") {
                this.wind_units_el.innerText = "m/s";
            }
        }
        this.wind_dir_el.innerText = this.dayWeather.wind_dir_icon;
    }


    startLightningTimer() {
        if (this.lightningTimeout) clearTimeout(this.lightningTimeout);
        if (['thunder', 'severe', 'hail'].includes(this.currentWeather.type)) {
            this.lightningTimeout = setTimeout(this.lightning.bind(this), Math.random() * 15000);
        }
    }

    lightning() {
        this.startLightningTimer();

        let pathX = 30 + Math.random() * (this.sizes.card.width - 60);
        let yOffset = 20;
        let steps = 20;
        let points = [pathX + ',0'];
        for (let i = 0; i < steps; i++) {
            let x = pathX + (Math.random() * yOffset - yOffset / 2);
            let y = this.sizes.card.height / steps * (i + 1);
            points.push(x + ',' + y);
        }

        let strike = document.createElementNS("http://www.w3.org/2000/svg", "path");

        // set the 'd' attribute (path commands) and other attributes
        strike.setAttribute("d", 'M' + points.join(' '));
        strike.setAttribute("fill", "none");
        strike.setAttribute("stroke", "white");
        strike.setAttribute("stroke-width", 2 + Math.random());

        // append the new path to the SVG
        this.innerSVG.appendChild(strike);


        // Flashing animation, will go back to original color when repeat number is odd
        let sky = document.getElementById("sky");
        let darkSky = document.getElementById("horizonNight");
        gsap.fromTo(sky, {filter: "brightness(105%)"}, {
            filter: "brightness(100%)",
            duration: 2,
            ease: Power4.easeOut
        });
        gsap.fromTo(darkSky, {filter: "brightness(140%)"}, {
            filter: "brightness(100%)",
            duration: 2,
            ease: Power4.easeOut
        });
        gsap.to(strike, {
            duration: 2,
            opacity: 0, ease: Power4.easeOut, onComplete: function () {
                strike.remove();
                strike = null;
            }
        });
    }

    reset() {
        weather_types.forEach(t => this.container.classList.remove(t));
    }

    tick(timestamp) {
        if (this.start === undefined)
            this.start = timestamp;
        const elapsed = timestamp - this.start;

        if (elapsed > 100) {
            if (this.rain_count < this.settings.rainCount) this.makeRain();
            if (this.flake_count < this.settings.snowCount) this.makeSnow();
            if (this.leaf_count < this.settings.leafCount) this.makeLeaf();
            if (this.hail_count < this.settings.hailCount) this.makeHail();
            if (this.cloud_count < this.settings.cloudCount) this.makeCloud();
            this.start = timestamp;
        }

        this.renderer.render(this.scene);
    }
}
